import React, { useState, useRef, useEffect } from "react";
import {
  AspectRatio,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
  useDisclosure,
  Flex,
  Tag,
  UnorderedList,
  ListItem,
  Tooltip,
} from "@chakra-ui/react";
import { includes } from "lodash";
import request from "../utils/request";
import CustomButton from "./CustomButton";
import Instructions from "./Instructions";
import placeholderImage from "../assests/place.png";

const STATUS = {
  APPROVE: "approve",
  REJECT: "reject",
};

const StudentForm = ({ formData, newForm, redirectUrl }) => {
  const [form, updateForm] = useState(() => {
    // Check if there's saved form data (from before payment redirect)
    const savedFormData = sessionStorage.getItem("enrollmentFormData");
    if (savedFormData && !formData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        console.log("=== RESTORING FORM DATA ===");
        console.log("Restored dob:", parsedData.dob);
        console.log("Full restored data:", parsedData);
        return parsedData;
      } catch (e) {
        console.error("Error parsing saved form data:", e);
      }
    }
    return formData || {
      firstName: "",
      lastName: "",
      gender: "male",
      medicalCondition: "",
      dob: "",
      profile: "",
      isPrevious: "false",
      islamicGradePrev: "1",
      iqraGradePrev: "1",
      fatherName: "",
      fatherEmail: "",
      fatherContact: "",
      motherName: "",
      motherEmail: "",
      motherContact: "",
      corresspondence: "father",
      corrEmail: "",
      homeAddress: "",
      ambulanceCover: "false",
      ambulanceMembershipNumber: "",
      refereeName: "",
      refereePhone: "",
      refereeEmail: "",
      enrollmentYear: "1",
      academicYear: "2026",
    };
  });
  const [tnc, setTnc] = useState(() => {
    // Check if T&C was already accepted (persists across page reload)
    return sessionStorage.getItem("tncAccepted") === "true";
  });
  const [isReadOnly, updateReadMode] = useState(!newForm);
  const [ignorePayment, setIgnorePayment] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(() => {
    // Check if payment was already completed (persists across page reload)
    if (sessionStorage.getItem("paymentComplete") === "true") {
      return true;
    }
    // Check if redirected back from eWay with success parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("payment") === "success") {
      sessionStorage.setItem("paymentComplete", "true");
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    }
    return false;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoggedIn = !!JSON.parse(localStorage.getItem("smart-alim:user"));
  const toast = useToast();
  const { isOpen: isSuccessModalOpen, onOpen: openSuccessModal, onClose: closeSuccessModal } = useDisclosure();
  const uploadButtonRef = useRef(null);

  // Save form data and T&C to sessionStorage whenever they change (for payment redirect)
  useEffect(() => {
    if (newForm) {
      sessionStorage.setItem("enrollmentFormData", JSON.stringify(form));
      sessionStorage.setItem("tncAccepted", tnc ? "true" : "false");
    }
  }, [form, tnc, newForm]);

  // Show toast and scroll to bottom if payment was just completed
  useEffect(() => {
    if (paymentComplete && newForm) {
      toast({
        title: "Payment Successful!",
        description: "Click 'Submit Enrollment' to complete your registration.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      // Scroll to bottom after a short delay to ensure page is rendered
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth"
        });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suppress cross-origin script errors from eWay
  useEffect(() => {
    const handleGlobalError = (event) => {
      // Suppress generic "Script error" from cross-origin scripts (eWay)
      if (event.message === "Script error." || event.message === "Script error") {
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener("error", handleGlobalError);
    return () => {
      window.removeEventListener("error", handleGlobalError);
    };
  }, []);

  // Listen for eWay payment completion
  useEffect(() => {
    const handlePaymentMessage = (event) => {
      console.log("Received message event:", event.origin, event.data);
      // Check if message is from eWay
      if (event.origin && event.origin.includes("ewaypayments.com")) {
        console.log("eWay message received:", event.data);
        console.log("Message type:", typeof event.data);

        // Check for various success indicators
        const data = event.data;
        const isSuccess =
          (typeof data === 'object' && data !== null && (
            data.transactionComplete === true ||
            data.ResultCode === "00" ||
            data.Success === true ||
            data.TransactionStatus === true
          )) ||
          (typeof data === 'string' && (
            data.includes("Success") ||
            data.includes("Approved") ||
            data.includes("00")
          ));

        const isError = typeof data === 'string' && data.includes("Error");

        if (isSuccess && !isError) {
          console.log("=== PAYMENT SUCCESS DETECTED ===");
          // Save to sessionStorage so it persists if page reloads
          sessionStorage.setItem("paymentComplete", "true");
          setPaymentComplete(true);
          toast({
            title: "Payment Successful!",
            description: "You can now submit your enrollment.",
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        } else if (isError) {
          console.log("=== PAYMENT ERROR ===", data);
          toast({
            title: "Payment Error",
            description: typeof data === 'string' ? data : "Payment failed. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }
      }
    };

    window.addEventListener("message", handlePaymentMessage);
    return () => {
      window.removeEventListener("message", handlePaymentMessage);
    };
  }, [toast]);

  // Auto-submit is disabled - user will manually click Submit after payment
  // Payment status is preserved in sessionStorage if page reloads

  // For all users: load eWay script into a hidden container (for payment flow)
  useEffect(() => {
    let timeoutId;

    const loadEwayScript = () => {
      try {
        console.log("=== LOADING EWAY SCRIPT ===");
        const hiddenContainer = document.getElementById("hidden-eway-container");
        const ewayPublicKey = process.env.REACT_APP_EWAY_PUBLIC_KEY;

        console.log("eWay public key:", ewayPublicKey ? "Present" : "Missing");
        console.log("Hidden container found:", !!hiddenContainer);

        if (!ewayPublicKey) {
          console.warn("eWay public key not configured");
          return;
        }

        if (hiddenContainer) {
          hiddenContainer.innerHTML = "";
          const script = document.createElement("script");

          script.src = "https://secure.ewaypayments.com/scripts/eCrypt.min.js";
          script.async = true;
          script.setAttribute("data-publicapikey", ewayPublicKey);
          script.setAttribute("class", "eway-paynow-button");
          script.setAttribute("data-amount", 18000);
          script.setAttribute("data-currency", "AUD");
          // Redirect back to this page with success parameter after payment
          script.setAttribute("data-resulturl", window.location.origin + "/enroll-student?payment=success");

          script.onload = () => {
            console.log("eWay script loaded successfully");
            console.log("Hidden container contents:", hiddenContainer.innerHTML);
          };

          // Add error handler to prevent uncaught errors
          script.onerror = () => {
            console.warn("eWay script failed to load");
          };

          hiddenContainer.appendChild(script);
          console.log("eWay script appended to container");
        }
      } catch (error) {
        console.warn("Error loading eWay script:", error);
      }
    };

    if (newForm && !paymentComplete && !ignorePayment) {
      // Small delay to ensure DOM is ready
      timeoutId = setTimeout(loadEwayScript, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [newForm, paymentComplete, ignorePayment]);

  //[todo] replace this with date-fns
  const date = new Date();
  const currDate = `${date.getFullYear()}-${
    date.getMonth() < 10 ? `0${date.getMonth()}` : date.getMonth()
  }-${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}`;

  const handleChange = (e) => {
    updateForm({ ...form, [e.target.name]: e.target.value });
  };

  const clickUploadButton = () => {
    uploadButtonRef.current.click();
  };

  const getFormatedData = () => {
    return {
      profile_photo: form["profile"],
      first_name: form["firstName"],
      last_name: form["lastName"],
      gender: form["gender"],
      medical_condition: form["medicalCondition"],
      dob: form["dob"],
      is_previous_student: form["isPrevious"] === "true",
      islamic_studies_grade_prev_year: parseInt(form["islamicGradePrev"]),
      iqra_grade_prev_year: parseInt(form["iqraGradePrev"]),
      enrolment_for_year: parseInt(form["enrollmentYear"]),
      academic_year: parseInt(form["academicYear"]),
      referee_name: form["refereeName"],
      referee_email_address: form["refereeEmail"],
      referee_phone_number: form["refereePhone"],
      father_name: form["fatherName"],
      mother_name: form["motherName"],
      father_email: form["fatherEmail"],
      mother_email: form["motherEmail"],
      father_contact_number: form["fatherContact"],
      mother_contact_number: form["motherContact"],
      ambulance_cover: form["ambulanceCover"] === "true",
      ambulance_membership_number: form["ambulanceMembershipNumber"],
      home_address: form["homeAddress"],
      preferred_contact_for_correspondence: form["corresspondence"],
      email_for_correspondence: form["corrEmail"],
      fees_paid: false,
      current_record: false,
      status: "pending",
      active_record: false,
    };
  };

  const updateFormStatus = async (status) => {
    try {
      const url = `/student/${status}/${form["id"]}/`;
      await request.post({ to: url });

      //show a toast here
      window.location.reload();
    } catch (error) {
      // Error handled silently
    }
  };

  const handleFormUpdate = async () => {
    if (isReadOnly === false) {
      const data = getFormatedData();
      console.log("=== UPDATE STUDENT ===");
      console.log("Student ID:", form["id"]);
      console.log("Update URL:", `/student/update/${form["id"]}/`);
      console.log("Data:", data);

      try {
        await request.post({
          to: `/student/update/${form["id"]}/`,
          body: data,
        });
        updateReadMode(!isReadOnly);
        toast({
          title: "Success",
          description: "Student details updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      } catch (error) {
        console.error("Update error:", error);
        toast({
          title: "Update Error",
          description: "Failed to update student details.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } else {
      updateReadMode(!isReadOnly);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];

    // validate if it's an image or not
    const fileName = file.name;
    var fileExtension = "";
    if (fileName.lastIndexOf(".") > 0) {
      fileExtension = fileName.substring(
        fileName.lastIndexOf(".") + 1,
        fileName.length
      );
    }

    if (includes(["png", "jpeg", "jpg"], fileExtension)) {
      const reader = new FileReader();
      reader.readAsDataURL(file); //converts file to an url
      reader.onloadend = () => {
        updateForm({ ...form, profile: reader.result });
      };
    } else {
      toast({
        description: "Only png, jpeg and jpg image files are allowed",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const validateForm = () => {
    const missingFields = [];

    // Required fields for all students
    if (!form.profile) missingFields.push("Profile Photo");
    if (!form.firstName?.trim()) missingFields.push("First Name");
    if (!form.lastName?.trim()) missingFields.push("Last Name");
    if (!form.dob) missingFields.push("Date of Birth");
    if (!form.medicalCondition?.trim()) missingFields.push("Medical Condition");
    if (!form.motherName?.trim()) missingFields.push("Mother's Name");
    if (!form.motherEmail?.trim()) missingFields.push("Mother's Email");
    if (!form.motherContact?.trim()) missingFields.push("Mother's Contact Number");
    if (!form.homeAddress?.trim()) missingFields.push("Home Address");
    if (!form.corrEmail?.trim()) missingFields.push("Correspondence Email");

    // Conditional fields for non-previous students
    if (form.isPrevious === "false") {
      if (!form.refereeName?.trim()) missingFields.push("Referee Name");
      if (!form.refereePhone?.trim()) missingFields.push("Referee Phone Number");
      if (!form.refereeEmail?.trim()) missingFields.push("Referee Email Address");
    }

    // Conditional field for ambulance cover
    if (form.ambulanceCover === "true") {
      if (!form.ambulanceMembershipNumber?.trim()) missingFields.push("Ambulance Membership Number");
    }

    // Terms and conditions
    if (!tnc) missingFields.push("Terms and Conditions Agreement");

    return missingFields;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("=== FORM SUBMIT START ===");
    console.log("Form data:", form);
    console.log("paymentComplete:", paymentComplete);
    console.log("isLoggedIn:", isLoggedIn);
    console.log("ignorePayment:", ignorePayment);
    console.log("tnc:", tnc);
    console.log("newForm:", newForm);

    // Check if payment is required but not completed
    // For guest users OR logged-in users without "Ignore Payment" checked
    // Allow if paymentComplete is true
    if (newForm && !paymentComplete && !(isLoggedIn && ignorePayment)) {
      console.log("Payment required but not completed - blocking form submission");
      toast({
        title: "Payment Required",
        description: "Please complete the payment using the Pay Now button below.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    console.log("Payment check passed - paymentComplete:", paymentComplete, "ignorePayment:", ignorePayment);

    // Validate form
    const missingFields = validateForm();
    if (missingFields.length > 0) {
      toast({
        title: "Required Fields Missing",
        description: `Please fill in: ${missingFields[0]}`,
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    console.log("API URL:", process.env.REACT_APP_API_URL);

    const data = getFormatedData();
    console.log("Submitting data:", JSON.stringify(data, null, 2));

    // Set loading state
    setIsSubmitting(true);

    try {
      console.log("Making API request to /student/create/...");
      const response = await request.post({
        to: "/student/create/",
        body: { ...data },
      });
      console.log("=== API RESPONSE ===");
      console.log("Full response:", response);
      console.log("response.data:", response.data);

      setIsSubmitting(false);
      // Clear payment status, form data, and T&C from sessionStorage
      sessionStorage.removeItem("paymentComplete");
      sessionStorage.removeItem("enrollmentFormData");
      sessionStorage.removeItem("tncAccepted");
      // Show success modal
      openSuccessModal();
    } catch (error) {
      console.error("=== SUBMIT ERROR ===");
      console.error("Error:", error);
      console.error("Error response:", error?.response);
      setIsSubmitting(false);
      toast({
        title: "Submission Error",
        description: "Failed to create student. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const Form = (
    <Box px="16">
      <>
        {!newForm ? (
          <Flex justifyContent="space-between" alignItems="center" w="100%">
            <Flex alignItems="center">
              <Heading color="brand.orange" as="h2" py={8}>
                {`${formData["firstName"]} ${formData["lastName"]}`}
              </Heading>
              <Tag
                ml={8}
                h="min-content"
                backgroundColor="brand.orange"
                color="white"
              >
                {formData["status"] || "pending"}
              </Tag>
            </Flex>
            <Flex alignItems="center">
              <Button
                backgroundColor="brand.orange"
                color="white"
                size="sm"
                borderRadius="md"
                onClick={handleFormUpdate}
                _hover={{}}
              >
                {isReadOnly ? "Edit Student Details" : "Save Student Details"}
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex alignItems="center">
            <Heading color="brand.orange" as="h2" py={8}>
              Enroll Student
            </Heading>
            {newForm && !paymentComplete && (
              <Tooltip label="Instructions" title="car">
                <Instructions />
              </Tooltip>
            )}
          </Flex>
        )}
      </>

      <form onSubmit={handleSubmit}>
        <Stack spacing="8">
          {/*---Personal Details---*/}
          <Box border="2px" p="4" borderRadius="md" borderColor="gray.200">
            <Text fontWeight="semibold">Personal Details</Text>
            <SimpleGrid columns={4} spacing={4} py="8">
              <Flex flexDirection="column" alignItems="flex-start">
                <Flex
                  flexDirection="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <FormControl id="profile">
                    <FormLabel>Profile photo</FormLabel>
                    <Input
                      type="file"
                      ref={uploadButtonRef}
                      display="none"
                      accept="image/*"
                      onChange={handleFile}
                      isReadOnly={isReadOnly}
                    />
                  </FormControl>
                  <AspectRatio w="200px" ratio={1}>
                    <Image
                      src={form["profile"] || placeholderImage}
                      objectFit="cover"
                    />
                  </AspectRatio>
                  <Button
                    variant="outline"
                    p="6"
                    mt="4"
                    color="brand.orange"
                    borderColor="brand.orange"
                    w="100%"
                    _hover={{}}
                    onClick={clickUploadButton}
                    tabIndex={1}
                  >
                    Upload Photo
                  </Button>
                </Flex>
              </Flex>

              <VStack spacing="4">
                {/**Firstname */}
                <FormControl id="first-name" isRequired>
                  <FormLabel>First name</FormLabel>
                  <Input
                    type="text"
                    name="firstName"
                    maxLength="50"
                    value={form["firstName"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={2}
                  />
                  <FormHelperText>Not more than 50 characters</FormHelperText>
                </FormControl>

                {/**Date of birth */}
                <FormControl id="date-of-birth" flex="1" isRequired>
                  <FormLabel>Date of Birth</FormLabel>
                  <Input
                    type="date"
                    name="dob"
                    max={currDate}
                    onChange={handleChange}
                    value={form["dob"]}
                    isReadOnly={isReadOnly}
                    tabIndex={5}
                  />
                </FormControl>

                {/**Is previous Student */}
                <FormControl id="is-previous-student">
                  <FormLabel>Is previous student ?</FormLabel>
                  <RadioGroup
                    value={form["isPrevious"]}
                    name="isPrevious"
                    colorScheme="orange"
                  >
                    <HStack spacing="8">
                      <Radio
                        isDisabled={
                          isReadOnly && form["isPrevious"] === "false"
                        }
                        onChange={handleChange}
                        value={"true"}
                        tabIndex={8}
                      >
                        Yes
                      </Radio>
                      <Radio
                        isDisabled={isReadOnly && form["isPrevious"] === "true"}
                        onChange={handleChange}
                        value={"false"}
                        tabIndex={8}
                      >
                        No
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
              </VStack>

              <VStack spacing="4">
                {/**lastname */}
                <FormControl id="last-name" isRequired>
                  <FormLabel>Last name</FormLabel>
                  <Input
                    type="text"
                    name="lastName"
                    maxLength="50"
                    value={form["lastName"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={3}
                  />
                  <FormHelperText>Not more than 50 characters</FormHelperText>
                </FormControl>

                {/**Medical condition */}
                <FormControl id="medical-condition" isRequired>
                  <FormLabel>Medical Condition</FormLabel>
                  <Textarea
                    name="medicalCondition"
                    value={form["medicalCondition"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    placeholder="Enter your medical condition"
                    tabIndex={6}
                  />
                </FormControl>

                {/**islamic studies grade */}
                {form.isPrevious === "true" && (
                  <FormControl id="islamic-grade" isRequired>
                    <FormLabel>Islamic studies grade in prev. year</FormLabel>
                    <Select
                      name="islamicGradePrev"
                      value={
                        form["islamicGradePrev"] ? form["islamicGradePrev"] : ""
                      }
                      onChange={handleChange}
                      isDisabled={isReadOnly}
                      tabIndex={9}
                    >
                      <option value="0">Year Prep</option>
                      <option value="1">Year 1</option>
                      <option value="2">Year 2</option>
                      <option value="3">Year 3</option>
                      <option value="4">Year 4</option>
                      <option value="5">Year 5</option>
                      <option value="6">Year 6</option>
                      <option value="7">Year 7</option>
                      <option value="8">Year 8</option>
                      <option value="9">Year 9</option>
                    </Select>
                  </FormControl>
                )}

                {form.isPrevious === "false" && (
                  <FormControl id="refree-name" isRequired>
                    <FormLabel>Referee Name</FormLabel>
                    <Input
                      type="text"
                      name="refereeName"
                      value={form["refereeName"]}
                      onChange={handleChange}
                      isReadOnly={isReadOnly}
                      tabIndex={9}
                    />
                  </FormControl>
                )}
              </VStack>

              <VStack spacing="4">
                {/**Gender */}
                <FormControl id="gender" flex="1" isRequired>
                  <FormLabel>Gender</FormLabel>
                  <Select
                    onChange={handleChange}
                    name="gender"
                    value={form["gender"]}
                    isDisabled={isReadOnly}
                    tabIndex={4}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Select>
                </FormControl>

                {/**Iqra grade */}
                {form.isPrevious === "true" && (
                  <FormControl id="igra-grade" isRequired>
                    <FormLabel>Iqra grade in prev. year</FormLabel>
                    <Select
                      name="iqraGradePrev"
                      value={form["iqraGradePrev"]}
                      onChange={handleChange}
                      isDisabled={isReadOnly}
                      tabIndex={7}
                    >
                      <option value="1">Iqra 1</option>
                      <option value="2">Iqra 2</option>
                      <option value="3">Iqra 3</option>
                      <option value="4">Iqra 4</option>
                      <option value="5">Iqra 5</option>
                      <option value="6">Iqra 6</option>
                      <option value="7">Senior</option>
                    </Select>
                  </FormControl>
                )}

                {form.isPrevious === "false" && (
                  <FormControl id="refree-phone" isRequired>
                    <FormLabel>Referee Phone number</FormLabel>
                    <Input
                      type="number"
                      name="refereePhone"
                      value={form["refereePhone"]}
                      onChange={handleChange}
                      isReadOnly={isReadOnly}
                      tabIndex={7}
                    />
                  </FormControl>
                )}
              </VStack>
            </SimpleGrid>

            {/**Row for Enrollment Year, Grade in School, and Referee Email Address - aligned */}
            {form.isPrevious === "false" && (
              <SimpleGrid columns={4} spacing={4} pt="4">
                <Box /> {/* Empty box to align with profile photo column */}
                <FormControl id="academic-year" isRequired>
                  <FormLabel>Enrollment Year</FormLabel>
                  <Select
                    name="academicYear"
                    value={form["academicYear"]}
                    onChange={handleChange}
                    isDisabled={isReadOnly}
                    tabIndex={10}
                  >
                    <option value="2026">2026</option>
                    <option value="2027">2027</option>
                    <option value="2028">2028</option>
                  </Select>
                </FormControl>
                <FormControl id="enrollment-year" isRequired>
                  <FormLabel>Grade in School</FormLabel>
                  <Select
                    name="enrollmentYear"
                    value={form["enrollmentYear"]}
                    onChange={handleChange}
                    isDisabled={isReadOnly}
                    tabIndex={11}
                  >
                    <option value="0">Kinder Garden</option>
                    <option value="1">Prep</option>
                    <option value="2">Year 1</option>
                    <option value="3">Year 2</option>
                    <option value="4">Year 3</option>
                    <option value="5">Year 4</option>
                    <option value="6">Year 5</option>
                    <option value="7">Year 6</option>
                    <option value="8">Year 7</option>
                    <option value="9">Year 8</option>
                    <option value="10">Year 9</option>
                    <option value="11">Year 10</option>
                  </Select>
                </FormControl>
                <FormControl id="refree-email" isRequired>
                  <FormLabel>Referee Email Address</FormLabel>
                  <Input
                    type="email"
                    name="refereeEmail"
                    value={form["refereeEmail"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={12}
                  />
                </FormControl>
              </SimpleGrid>
            )}
          </Box>

          {/*---Parent Detail---*/}
          <Box border="2px" p="4" borderRadius="md" borderColor="gray.200">
            <Text fontWeight="semibold">Parent Details</Text>
            <SimpleGrid columns={3} spacing={4} py="8">
              <VStack spacing="4">
                {/**father name */}
                <FormControl id="father-name">
                  <FormLabel>Father's name</FormLabel>
                  <Input
                    type="text"
                    name="fatherName"
                    maxLength="50"
                    value={form["fatherName"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={13}
                  />
                  <FormHelperText>Not more than 50 characters</FormHelperText>
                </FormControl>

                {/**mother name */}
                <FormControl id="mother-name" isRequired>
                  <FormLabel>Mother's name</FormLabel>
                  <Input
                    type="text"
                    name="motherName"
                    maxLength="50"
                    value={form["motherName"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={16}
                  />
                  <FormHelperText>Not more than 50 characters</FormHelperText>
                </FormControl>

                {/**correspondence */}
                <FormControl id="corresspondence">
                  <FormLabel>Preferred contact for correspondence</FormLabel>
                  <RadioGroup
                    value={form["corresspondence"]}
                    name="corresspondence"
                    colorScheme="orange"
                  >
                    <HStack spacing="5">
                      <Radio
                        isDisabled={
                          isReadOnly && form["corresspondence"] === "mother"
                        }
                        value="father"
                        onChange={handleChange}
                        tabIndex={19}
                      >
                        Father
                      </Radio>
                      <Radio
                        isDisabled={
                          isReadOnly && form["corresspondence"] === "father"
                        }
                        value="mother"
                        onChange={handleChange}
                        tabIndex={19}
                      >
                        Mother
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>

                {/**ambulance cover */}
                <FormControl id="ambulance-cover">
                  <FormLabel>Ambulance Cover</FormLabel>
                  <RadioGroup
                    value={form["ambulanceCover"]}
                    name="ambulanceCover"
                    colorScheme="orange"
                  >
                    <HStack spacing="8">
                      <Radio
                        isDisabled={
                          isReadOnly && form["ambulanceCover"] === "false"
                        }
                        value={"true"}
                        onChange={handleChange}
                        tabIndex={22}
                      >
                        Yes
                      </Radio>
                      <Radio
                        isDisabled={
                          isReadOnly && form["ambulanceCover"] === "true"
                        }
                        value={"false"}
                        onChange={handleChange}
                        tabIndex={22}
                      >
                        No
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
                {form.ambulanceCover === "true" && (
                  <FormControl id="ambulanceMembershipNumber" isRequired>
                    <FormLabel>Ambulance Membership Number</FormLabel>
                    <Input
                      type="text"
                      name="ambulanceMembershipNumber"
                      value={form["ambulanceMembershipNumber"]}
                      onChange={handleChange}
                      isReadOnly={isReadOnly}
                      tabIndex={23}
                    />
                  </FormControl>
                )}
              </VStack>

              <VStack spacing="4">
                {/**Father email */}
                <FormControl id="father-email">
                  <FormLabel>Father's email</FormLabel>
                  <Input
                    type="email"
                    name="fatherEmail"
                    value={form["fatherEmail"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={14}
                  />
                  <FormHelperText>Enter valid email id</FormHelperText>
                </FormControl>

                {/**Mother email */}
                <FormControl id="mother-email" isRequired>
                  <FormLabel>Mother's email</FormLabel>
                  <Input
                    type="email"
                    name="motherEmail"
                    value={form["motherEmail"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={17}
                  />
                  <FormHelperText>Enter valid email id</FormHelperText>
                </FormControl>

                {/**Corrosspondence email */}
                <FormControl id="corr-email" isRequired>
                  <FormLabel>Correspondence's email</FormLabel>
                  <Input
                    type="email"
                    name="corrEmail"
                    value={form["corrEmail"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={20}
                  />
                </FormControl>
              </VStack>

              <VStack spacing="4">
                {/**Father contact number */}
                <FormControl id="father-contact">
                  <FormLabel>Father Contact Number</FormLabel>
                  <Input
                    type="number"
                    name="fatherContact"
                    value={form["fatherContact"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={15}
                  />
                  <FormHelperText>&nbsp;</FormHelperText>
                </FormControl>

                {/**Mother Contact number */}
                <FormControl id="mother-contact" isRequired>
                  <FormLabel>Mother Contact Number</FormLabel>
                  <Input
                    type="number"
                    name="motherContact"
                    value={form["motherContact"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={18}
                  />
                  <FormHelperText>&nbsp;</FormHelperText>
                </FormControl>

                {/**Home Address */}
                <FormControl id="home-address" isRequired>
                  <FormLabel>Home Address</FormLabel>
                  <Textarea
                    name="homeAddress"
                    value={form["homeAddress"]}
                    onChange={handleChange}
                    isReadOnly={isReadOnly}
                    tabIndex={21}
                  />
                </FormControl>
              </VStack>
            </SimpleGrid>
          </Box>

          <SimpleGrid columns={1} spacing="4">
            {/* Fee Details section - only show for existing records OR logged-in users on new form */}
            {(!newForm || isLoggedIn) && (
              <Box border="2px" p="4" borderRadius="md" borderColor="gray.200">
                {!newForm ? (
                  <>
                    <Text fontWeight="semibold">Fee Details</Text>
                    <SimpleGrid columns={3} spacing={8} py={8}>
                      <FormControl id="paidAmount" isReadOnly>
                        <FormLabel>Paid Amount</FormLabel>
                        <Input type="text" name="paidAmount" />
                      </FormControl>
                      <FormControl id="feeStatus" isReadOnly>
                        <FormLabel>Status</FormLabel>
                        <Input type="text" name="feeStatus" />
                      </FormControl>
                      <FormControl id="invoiceDate" isReadOnly>
                        <FormLabel>Invoice Date</FormLabel>
                        <Input type="text" name="invoiceDate" />
                      </FormControl>
                    </SimpleGrid>
                  </>
                ) : (
                  <VStack align="flex-start" spacing={4}>
                    {!paymentComplete && (
                      <Checkbox
                        colorScheme="orange"
                        isChecked={ignorePayment}
                        onChange={(e) => setIgnorePayment(e.target.checked)}
                      >
                        Ignore Payment
                      </Checkbox>
                    )}
                    {paymentComplete && (
                      <Text color="green.500" fontWeight="semibold">
                        Payment completed successfully!
                      </Text>
                    )}
                  </VStack>
                )}
              </Box>
            )}

            {/**Agree and Submit */}
            {newForm && (
              <Box border="2px" p="4" borderRadius="md" borderColor="gray.200">
                <Text fontWeight="semibold">Agree and Submit</Text>
                <Box py="8" px={16} textAlign="left">
                  <VStack alignItems="flex-start">
                    <Heading as="h3" fontSize="md">
                      NOTE THE FOLLOWING IMPORTANT INFORMATION:
                    </Heading>
                    <UnorderedList>
                      <ListItem>
                        The Al-Asr School academic (teaching) session will begin
                        from Sunday 1st February 2026 and the school will run
                        every Sunday.
                      </ListItem>
                      <ListItem>
                        School Timings will be as follows: Start at 10:15 am and
                        End at 1:45 pm. The annual fee for the year 2026 will be
                        $180.00 AUD . The fees are to be paid at the time of
                        enrolment. Late enrolment fees paid after 01/02/2026
                        will incur an additional charge of $35.00.{" "}
                      </ListItem>
                      <ListItem>
                        The Application Fee is non-refundable.
                      </ListItem>
                      <ListItem>
                        Parents/Guardians who sign the Application for Enrolment
                        are jointly and severely liable for payment of all of
                        the School's fees and charges in relation to the student
                        (Fees and Charges
                      </ListItem>
                      <ListItem>
                        Bank Details: AL-ASR School, BSB:083337,
                        Account:940113371.
                      </ListItem>
                    </UnorderedList>

                    <Heading as="h3" fontSize="md">
                      Terms And Conditions
                    </Heading>
                    <UnorderedList>
                      <ListItem>
                        I agree to be solely responsible for my child on the
                        School's Premises.
                      </ListItem>
                      <ListItem>
                        I agree to make myself available to any other volunteer
                        rosters as requested by Al-Asr School
                      </ListItem>
                      <ListItem>
                        I understand that I should direct and discuss all my
                        complaints/grievances/concerns only with the Al-Asr
                        Management and not with any other staff/volunteer
                        directly.
                      </ListItem>
                      <ListItem>
                        I understand that I am responsible for what my child
                        eats and drinks at the school.
                      </ListItem>
                      <ListItem>
                        I understand that the School reserves the right not to
                        refund Fees and Charges. However, the Management may, in
                        their sole discretion, consider a request for a refund
                        by a Parent/Guardian in accordance with the School's
                        Refund Policy
                      </ListItem>
                      <ListItem>
                        I understand that the Al-Asr Management reserves the
                        right to expel my child on behavioural and attendance
                        grounds
                      </ListItem>
                      <ListItem>
                        I take responsibility for my child in using ethical
                        language and demostrating responsible behavior with all
                        staff and students at all time.
                      </ListItem>
                      <ListItem>
                        I understand that if my child is absent during the term
                        for more than two weeks due to illness/overseas trips, I
                        will inform Al-Asr Madrassa management in advance
                        through email on madressa@alasr.com.au.
                      </ListItem>
                      <ListItem>
                        I understand that if my child does not attend for a term
                        or part of a term due to any reasons i.e. holidays, I
                        must still pay the term fees in order to reserve their
                        spot in the school.
                      </ListItem>
                      <ListItem>
                        I agree that I will bring my child to school on time.
                      </ListItem>
                      <ListItem>
                        I agree that I am responsible to ensure that my child is
                        not Najis while at the center. If the child needs
                        assistance to use the toilet I will be available to help
                        my child.
                      </ListItem>
                      <ListItem>
                        I agree that my child will always attend the school in
                        the proper Islamic Dress Code (long shirts, long sleeves
                        and long/loose pants for all children above 9 years,
                        Headscarf for all girls should be long enough and
                        appropriate for Hijaab). No leggings or tights for girls
                        and no shorts for boys.
                      </ListItem>
                      <ListItem>
                        I understand that I must attend each parent-teacher
                        meeting when advised by the school.
                      </ListItem>
                      <ListItem>
                        I understand that if my child is found to be
                        using/displaying Video Games, Consoles, Ipods, Mobile
                        Phones or similar devices during school hours, the
                        devices will be confiscated.
                      </ListItem>
                      <ListItem>
                        Al-Asr school will call an ambulance in case of
                        emergency/life-threatening injuries and any cost
                        incurred will be borne by the concerned parent.
                      </ListItem>
                      <ListItem>
                        I agree that all copyright for all the work and
                        intellectual property produced by the student in
                        relation to his/her participation in the Madrassa
                        program shall be assigned and belong to Al-Asr.
                      </ListItem>
                      <ListItem>
                        I consent for my child's name/photographs/videos to be
                        included in Al-Asr Madrassa's social media and email
                        group.
                      </ListItem>
                      <ListItem>
                        I consent to administer first aid treatment/Panadol to
                        my child in case of an emergency during school hours.
                      </ListItem>
                    </UnorderedList>

                    <Text>
                      For further information please contact
                      madressa@alasr.com.au.
                    </Text>

                    <Checkbox
                      colorScheme="orange"
                      alignItems="start"
                      isChecked={tnc}
                      onChange={(e) => setTnc(e.target.checked)}
                      fontWeight="semibold"
                      tabIndex={24}
                    >
                      I HAVE READ ALL THE TERMS AND CONDITIONS ABOVE, AND AGREE
                      TO ABIDE BY THEM. COMPLETING THIS FORM DOES NOT GUARANTEE
                      ENROLMENT INTO THE SCHOOL.
                    </Checkbox>
                  </VStack>
                  {/* Submit section for all users */}
                  <Box mt="8" mb="2" w="100%">
                    {paymentComplete ? (
                      /* Payment completed - show Submit button */
                      <VStack spacing={3} w="100%">
                        <Text color="green.500" fontWeight="semibold" textAlign="center">
                          Payment successful! Click Submit to complete your enrollment.
                        </Text>
                        <Button
                          type="submit"
                          variant="solid"
                          backgroundColor="green.500"
                          color="white"
                          w="100%"
                          size="lg"
                          _hover={{ backgroundColor: "green.600" }}
                          isDisabled={isSubmitting}
                          tabIndex={25}
                          onClick={() => console.log("Green Submit button clicked - isSubmitting:", isSubmitting)}
                        >
                          {isSubmitting ? (
                            <HStack spacing={2}>
                              <Spinner size="sm" />
                              <Text>Submitting...</Text>
                            </HStack>
                          ) : (
                            "Submit Enrollment"
                          )}
                        </Button>
                      </VStack>
                    ) : isLoggedIn && ignorePayment ? (
                      /* Logged-in user with Ignore Payment checked - direct submit */
                      <Button
                        type="submit"
                        variant="solid"
                        backgroundColor="brand.orange"
                        color="white"
                        w="100%"
                        _hover={{}}
                        isDisabled={!tnc || isSubmitting}
                        tabIndex={25}
                        onClick={() => console.log("Submit button clicked")}
                      >
                        {isSubmitting ? (
                          <HStack spacing={2}>
                            <Spinner size="sm" />
                            <Text>Submitting...</Text>
                          </HStack>
                        ) : (
                          "Submit"
                        )}
                      </Button>
                    ) : (
                      /* Guest users OR logged-in users without Ignore Payment - payment required */
                      <VStack spacing={4} w="100%" bg="yellow.50" p={4} borderRadius="md">
                        <Heading size="sm" color="brand.orange">Payment Required</Heading>
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                          Please complete the payment of <strong>$180.00 AUD</strong> to submit your enrollment.
                        </Text>
                        {/* eWay button container - visible */}
                        <Box
                          id="hidden-eway-container"
                          w="100%"
                          minH="60px"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          border="3px dashed"
                          borderColor={tnc ? "orange.400" : "gray.300"}
                          borderRadius="md"
                          p={4}
                          bg={tnc ? "white" : "gray.100"}
                          opacity={tnc ? 1 : 0.5}
                          pointerEvents={tnc ? "auto" : "none"}
                          sx={{
                            "& button, & iframe": {
                              minWidth: "200px",
                              minHeight: "40px",
                            }
                          }}
                        >
                          {!tnc ? (
                            <Text color="gray.500" fontSize="sm" textAlign="center">
                              Accept Terms & Conditions above to enable payment button
                            </Text>
                          ) : (
                            <Text color="gray.400" fontSize="sm" textAlign="center">
                              eWay Payment Button Loading...
                            </Text>
                          )}
                        </Box>
                        {!tnc && (
                          <Text fontSize="xs" color="red.500">
                            * Please check the Terms and Conditions checkbox above to proceed with payment
                          </Text>
                        )}
                      </VStack>
                      )}
                    </Box>
                </Box>
              </Box>
            )}
          </SimpleGrid>

          {form["status"] === "pending" && isReadOnly === true && (
            <Box border="2px" p="4" borderRadius="md" borderColor="gray.200">
              <Text fontWeight="semibold">Application Status</Text>
              <Flex justifyContent="center" alignItems="center" py={8}>
                <Flex justifyContent="center" w="50%" mx={12}>
                  <CustomButton
                    label={"Accept"}
                    handleFuction={() => updateFormStatus(STATUS.APPROVE)}
                    style={{
                      variant: "solid",
                      backgroundColor: "green.500",
                      color: "white",
                      w: "100%",
                    }}
                  />
                </Flex>
                <Flex justifyContent="center" w="50%" mx={12}>
                  <CustomButton
                    label={"reject"}
                    handleFuction={() => updateFormStatus(STATUS.REJECT)}
                    style={{
                      variant: "solid",
                      backgroundColor: "red.500",
                      color: "white",
                      w: "100%",
                    }}
                  />
                </Flex>
              </Flex>
            </Box>
          )}
        </Stack>
      </form>

      {/* Success Modal */}
      <Modal isOpen={isSuccessModalOpen} onClose={closeSuccessModal} isCentered closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="green.500">Success!</ModalHeader>
          <ModalBody>
            <Text>Student has been enrolled successfully.</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              backgroundColor="brand.orange"
              color="white"
              _hover={{}}
              onClick={() => {
                closeSuccessModal();
                window.location.href = "/";
              }}
            >
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
  return [Form, form];
};

export default StudentForm;
